import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import type { IResourceDatabaseFolder } from '@/api/types';
import type { IDatabaseFolderColumnNames } from '@/components/common';

import { databaseFolderSortingFunctions } from '@/helpers';
import { useTableSort } from '@/hooks';
import { ResourceDatabasesTable } from './ResourceDatabasesTable';
import { DatabaseStatus } from '@/api/enums';
import { ResourcesNoData } from '@/components/common';
import routes from '@/config/routes';

interface IProps {
  items: IResourceDatabaseFolder[];
  clientId: number;
}

const ResourceDatabasesContainer = ({ items, clientId }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { sortedData, onSort, setRawData, sortingState } = useTableSort<
    IDatabaseFolderColumnNames,
    IResourceDatabaseFolder
  >({
    initial: { name: 'DESC' },
    sortingFunctions: databaseFolderSortingFunctions,
  });
  const history = useHistory();

  useEffect(() => {
    const itemsWithStatus = items.map(item => {
      return {
        ...item,
        // TODO: Add proper status info when available
        status: DatabaseStatus.Active,
      };
    });
    setRawData(itemsWithStatus);
  }, [items, setRawData]);

  const selectDatabaseAction = useCallback(
    (dbId: number) => {
      history.push(generatePath(routes.sp.scheduling.resources.database, { client: clientId, dbId: dbId }));
    },
    [clientId, history],
  );

  if (!items?.length) return <ResourcesNoData label={t`resources.content.noDatabases`} />;

  return (
    <ResourceDatabasesTable
      items={sortedData}
      sortingState={sortingState}
      onSort={onSort}
      selectDatabaseAction={selectDatabaseAction}
    />
  );
};

export { ResourceDatabasesContainer };
